import { useState, useEffect } from 'react';
import { clientsApi, parksApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/DataTable';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [parks, setParks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    park_id: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsData, parksData] = await Promise.all([
        clientsApi.getAll(),
        parksApi.getAll(),
      ]);
      setClients(clientsData);
      setParks(parksData);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', address: '', park_id: '', notes: '' });
    setEditingClient(null);
  };

  const openDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        park_id: client.park_id || '',
        notes: client.notes || '',
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await clientsApi.update(editingClient.id, formData);
        toast.success('Client updated');
      } else {
        await clientsApi.create(formData);
        toast.success('Client created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save client');
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await clientsApi.delete(client.id);
      toast.success('Client deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const getParkName = (parkId) => {
    const park = parks.find(p => p.id === parkId);
    return park?.name || '-';
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'address', label: 'Address', render: (val) => val || '-' },
    { key: 'park_id', label: 'Park', render: (val) => getParkName(val) },
    { key: 'created_at', label: 'Created', render: (val) => val ? format(new Date(val), 'MMM d, yyyy') : '-' },
  ];

  const actions = [
    { label: 'Edit', icon: Pencil, onClick: openDialog },
    { label: 'Delete', icon: Trash2, onClick: handleDelete, destructive: true },
  ];

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="text-sm text-stone-500 mt-1">Manage your client directory</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-client-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Client
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={clients}
        actions={actions}
        isLoading={isLoading}
        emptyMessage="No clients found. Add your first client!"
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="client-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="client-phone-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                data-testid="client-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="park">Park/Site</Label>
              <Select value={formData.park_id} onValueChange={(val) => setFormData({ ...formData, park_id: val })}>
                <SelectTrigger data-testid="client-park-select">
                  <SelectValue placeholder="Select park" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {parks.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="client-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="client-submit-btn">
                {editingClient ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
